import React, { useState } from 'react';
import UISimpleTable, {
  SimpleTableColumn,
} from '@/components/shared/UISimpleTable';
import UIIconButton from '@/components/shared/UIIconButton';
import UITenantInfo from '@/components/shared/UITenantInfo';
import TenantActionButtons from '@/components/shared/TenantActionButtons';
import { TenantData } from '../ListOfDebtorsSection/ListOfDebtorsSection';
import { ReactComponent as DotsIcon } from '@/img/dots.svg';
import './DebtorsTable.scss';
import DebtorDetailsModal from '../DebtorDetailsModal';

export interface SortState {
  column: string | null;
  direction: 'asc' | 'desc' | null;
}

interface DebtorsTableProps {
  data?: TenantData[];
  onTenantAction?: (tenantId: string, action: string) => void;
  loading?: boolean;
}

const baseClass = 'DebtorsTable';

const mockHandlers = {
  onTenantAction: (tenantId: string, action: string) =>
    console.log('Action:', action, 'for tenant:', tenantId),
};

const DebtorsTable: React.FC<DebtorsTableProps> = ({
  data = [],
  onTenantAction = mockHandlers.onTenantAction,
  loading = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });

  const handleSort = (columnKey: string, direction: 'asc' | 'desc') => {
    setSortState({ column: columnKey, direction });
  };

  // Обробник кліку на кнопку warning
  const handleWarningClick = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setIsModalOpen(true);
  };

  // Конфігурація стовпців
  const columns: SimpleTableColumn[] = [
    {
      key: 'name',
      label: 'TENANT',
      width: '385px',
      sortable: true,
      sortDirection: sortState.column === 'name' ? sortState.direction : null,
      showDivider: true,
      render: (_, tenant: TenantData) => {
        const tenantId =
          typeof tenant.id === 'string' ? parseInt(tenant.id, 10) : tenant.id;

        return (
          <div className={`${baseClass}__tenant-content`}>
            <UITenantInfo
              name={tenant.name}
              email={tenant.email}
              tenantId={tenantId}
              avatar={tenant.avatar || null}
            />

            <UIIconButton
              variant="danger"
              size="medium"
              iconType="warning"
              onClick={() => handleWarningClick(tenant.id)}
              className={`${baseClass}__warning-button`}
            />
          </div>
        );
      },
    },
    {
      key: 'debt',
      label: 'DEBT',
      width: '95px',
      sortable: true,
      sortDirection: sortState.column === 'debt' ? sortState.direction : null,
      showDivider: true,
      render: (debt, tenant: TenantData) => (
        <div className={`${baseClass}__debt-amount`}>
          {tenant.currency}
          {debt}
        </div>
      ),
    },
    {
      key: 'property',
      label: 'PROPERTY',
      width: '200px',
      sortable: true,
      sortDirection: sortState.column === 'property' ? sortState.direction : null,
      showDivider: true,
      render: (property) => (
        <div className={`${baseClass}__property-name`}>{property}</div>
      ),
    },
    {
      key: 'unit',
      label: 'NUMBER',
      width: '114px',
      sortable: true,
      sortDirection: sortState.column === 'unit' ? sortState.direction : null,
      showDivider: true,
      render: (unit) => (
        <div className={`${baseClass}__unit-number`}>{unit}</div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '174px',
      showDivider: false,
      render: (_, tenant: TenantData) => (
        <TenantActionButtons
          tenantId={tenant.id}
          onAction={onTenantAction}
          className={`${baseClass}__actions-group`}
        />
      ),
    },
    {
      key: 'menu',
      label: 'Menu',
      width: '48px',
      headerContent: <DotsIcon className={`${baseClass}__menu-dots`} />,
      render: () => <DotsIcon className={`${baseClass}__menu-dots`} />,
    },
  ];

  return (
    <>
      <UISimpleTable
        data={data}
        columns={columns}
        className={baseClass}
        getRowKey={(tenant) => tenant.id}
        onSort={handleSort}
      />

      <DebtorDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tenantId={selectedTenantId}
      />
    </>
  );
};

export default DebtorsTable;